import { MantineProvider } from "@mantine/core";
import { createContext, useContext, useState, type ReactNode } from "react";
import { cssVariablesResolver, theme } from "@/theme";
import { beloraCssVariablesResolver, beloraTheme } from "@/theme.belora";

const STORAGE_KEY = "yalana-theme";
const VALID_THEMES = ["default", "belora"] as const;
export type AppThemeName = (typeof VALID_THEMES)[number];

function getStoredTheme(): AppThemeName {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && (VALID_THEMES as readonly string[]).includes(stored)) {
      return stored as AppThemeName;
    }
  } catch {
    // localStorage unavailable
  }
  return "belora";
}

type AppThemeContextValue = {
  themeName: AppThemeName;
  setThemeName: (name: AppThemeName) => void;
};

const AppThemeContext = createContext<AppThemeContextValue | null>(null);

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [themeName, setThemeNameState] = useState<AppThemeName>(getStoredTheme);

  function setThemeName(name: AppThemeName) {
    try {
      localStorage.setItem(STORAGE_KEY, name);
    } catch {
      // localStorage unavailable
    }
    setThemeNameState(name);
  }

  const activeTheme = themeName === "belora" ? beloraTheme : theme;
  const activeResolver =
    themeName === "belora" ? beloraCssVariablesResolver : cssVariablesResolver;

  return (
    <AppThemeContext.Provider value={{ themeName, setThemeName }}>
      <MantineProvider
        theme={activeTheme}
        cssVariablesResolver={activeResolver}
        defaultColorScheme="auto"
      >
        {children}
      </MantineProvider>
    </AppThemeContext.Provider>
  );
}

export function useAppTheme(): AppThemeContextValue {
  const ctx = useContext(AppThemeContext);
  if (!ctx) throw new Error("useAppTheme must be used within AppThemeProvider");
  return ctx;
}
